// ----------------------------------------------------------------------- react
import { useCallback, useRef, useState } from "react";

// ----------------------------------------------------------------------- internos
import { analizarCargas, aplicarCargas } from "../services/planillaCargas";
import { retener, PORQUE_NO_SE_PUDO_LEER } from "../services/archivoRetenido";

/* Subir la «Planilla diaria de cargas» y dejar la Previsión armada.
 *
 * ── DE DÓNDE SALIÓ ────────────────────────────────────────────────────────
 *
 * Igna, 31/08: intentaron subir este Excel por la pantalla de Movimientos y
 * perdieron la mañana — es OTRO archivo. Tiene una pestaña por día (192 en el
 * archivo real) y cuenta los viajes que se están ARMANDO, así que va a
 * Previsión, no a Movimientos. FlotaBot elige solo la pestaña más nueva y
 * descarta las otras 191: nadie tiene que borrar pestañas antes de subir.
 *
 * ── POR QUÉ IMPORTA MÁS DE LO QUE PARECE ──────────────────────────────────
 *
 * De la Previsión sale el certificado de ART (#122): al confirmar acá, cada
 * viaje con chofer y cliente pide el suyo — ANTES de que el camión salga, que
 * es el único momento en que el papel sirve.
 *
 * ── QUÉ SE MUESTRA Y QUÉ NO ───────────────────────────────────────────────
 *
 * Lo nuevo viene tildado: es lo que se vino a hacer. Lo que ya está en
 * Previsión se cuenta y no se lista. Los CARGADOS de la hoja se descartan
 * —son historia de Movimientos— y los PENDIENTES se muestran aparte y no se
 * cargan: no tienen fecha, y una previsión sin fecha no existe.
 *
 * Los marcadores del papel viajan en la nota, textuales: «(VELOZZO)»,
 * «SARDINA??», «BI TREN», «en rojo en la planilla». La pantalla no los
 * interpreta — los muestra, que es distinto.
 */

const vacio = (v) => (v === "" || v === null || v === undefined ? "—" : v);

/* «12 cargas a Previsión (3 pisaron la versión anterior), 8 certificados
 * de ART pedidos.» Las pisadas (02/09) cuentan adentro del total: para el
 * que subió, las 12 son cargas que quedaron — el paréntesis dice cuántas
 * escribieron encima en vez de sumar fila. */
const resumenDe = (h) => {
  const pisadas = h.pisadas || 0;
  const n = (h.insertados || 0) + pisadas;
  const uno = `${n} ${n === 1 ? "carga" : "cargas"} a Previsión`;
  const pisa = pisadas
    ? ` (${pisadas} ${pisadas === 1 ? "pisó la versión anterior" : "pisaron la versión anterior"})`
    : "";
  const art = h.art_pedidas
    ? `, ${h.art_pedidas} ${h.art_pedidas === 1 ? "certificado de ART pedido" : "certificados de ART pedidos"}`
    : "";
  return `${uno}${pisa}${art}.`;
};

/* La fecha como la dice la planilla: 31/8, no 2026-08-31. */
const diaDe = (iso) => {
  const [, m, d] = String(iso || "").split("-");
  return m && d ? `${Number(d)}/${Number(m)}` : vacio(iso);
};

const SubirCargas = () => {
  const [archivo, setArchivo] = useState(null);
  const [analisis, setAnalisis] = useState(null);
  const [mirando, setMirando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [encima, setEncima] = useState(false);
  const [nuevas, setNuevas] = useState(() => new Set());
  /* La cajita (pedido de Igna, 01/09): con el archivo elegido y ANTES de
   * mostrar nada, se puede decir en criollo qué se quiere — «cargar los
   * viajes de mañana», «qué falta del 31/8». La entiende FlotaBot con
   * reglas, sin IA; vacía, carga todo como siempre. */
  const [pedido, setPedido] = useState("");
  const [eligiendo, setEligiendo] = useState(false);
  const entrada = useRef(null);

  const limpiar = () => {
    setAnalisis(null);
    setError(null);
    setHecho(null);
    setNuevas(new Set());
  };

  const analizar = useCallback(async (f, p) => {
    setEligiendo(false);
    limpiar();
    setMirando(true);
    const r = await analizarCargas(f, p);
    setMirando(false);
    if (!r.ok) {
      setError(r.mensaje || r.error || "No se pudo leer el archivo.");
      // El «no entendí» del pedido se corrige reescribiendo, no re-subiendo:
      // la cajita queda abierta con el texto como estaba.
      setEligiendo(true);
      return;
    }
    setAnalisis(r);
    // En «qué falta», lo que YA VIAJA no viene tildado: cargarlo duplicaría
    // un viaje que existe. Se puede tildar a mano igual.
    setNuevas(
      new Set(
        (r.nuevas || [])
          .filter((n) => !n.en_movimientos)
          .map((n) => n.clave),
      ),
    );
  }, []);

  /* Los bytes se leen al ELEGIR, y lo que queda guardado es esa copia.
   *
   * Acá la ventana es todavía más larga que en movimientos: entre elegir el
   * archivo y confirmar hay un paso más —la cajita del pedido— y después la
   * lista para tildar. En todo ese rato el Excel puede abrirse, guardarse o
   * moverse, y un `File` es un puntero al disco, no el archivo. Cuando el
   * puntero queda muerto el `fetch` se cae antes de mandar nada y desde
   * afuera se ve como «no anda». Ver `archivoRetenido`. */
  const tomar = async (f) => {
    limpiar();
    let copia;
    try {
      copia = await retener(f);
    } catch {
      setArchivo(null);
      setError(PORQUE_NO_SE_PUDO_LEER);
      return;
    }
    setArchivo(copia);
    setEligiendo(true);
  };

  const elegido = (e) => {
    const f = e.target.files?.[0];
    if (f) tomar(f);
  };

  const soltar = (e) => {
    e.preventDefault();
    setEncima(false);
    const f = e.dataTransfer.files?.[0];
    if (f) tomar(f);
  };

  const alternar = (clave) => {
    const s = new Set(nuevas);
    if (s.has(clave)) s.delete(clave);
    else s.add(clave);
    setNuevas(s);
  };

  const confirmar = async () => {
    setCargando(true);
    const r = await aplicarCargas(archivo, [...nuevas]);
    setCargando(false);
    if (!r.ok) {
      setError(r.mensaje || r.error || "No se pudo cargar.");
      return;
    }
    setAnalisis(null);
    setHecho(r);
  };

  return (
    <div>
      <p className="tk-meta">
        Subí el Excel de la <b>Planilla diaria de cargas</b> — con todas sus
        pestañas, FlotaBot usa la más nueva — y la previsión queda cargada.
        Lo que ya está igual no se toca; lo que <b>cambió</b> en la planilla
        pisa la versión cargada.
      </p>

      <div
        className={"planilla-cajon" + (encima ? " planilla-cajon--encima" : "")}
        onDragOver={(e) => {
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={soltar}
      >
        <p className="planilla-cajon__txt">
          Arrastrá la planilla acá
          <br />
          <span className="tk-meta">o</span>
        </p>
        <button
          type="button"
          className="tk-boton"
          onClick={() => entrada.current?.click()}
        >
          Buscar en el equipo
        </button>
        <input
          ref={entrada}
          type="file"
          accept=".xlsx,.xlsm"
          onChange={elegido}
          hidden
          aria-label="Planilla diaria de cargas"
        />
        {archivo && <p className="tk-meta">{archivo.name}</p>}
      </div>

      {eligiendo && archivo && (
        <div className="panel-aviso suave">
          <h3>Indicá qué querés hacer con esta planilla</h3>
          <p className="tk-meta">
            Por ejemplo: «cargar los viajes de mañana» · «qué falta del 31/8»
            · «qué falta de hoy». Vacío = cargar todo lo que traiga.
          </p>
          <input
            value={pedido}
            onChange={(e) => setPedido(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") analizar(archivo, pedido);
            }}
            placeholder="cargar los viajes de mañana"
            aria-label="Qué querés hacer con esta planilla"
            style={{ width: "100%", maxWidth: 420 }}
            autoFocus
          />{" "}
          <button
            type="button"
            className="tk-boton tk-boton--fuerte"
            onClick={() => analizar(archivo, pedido)}
          >
            Dale
          </button>
        </div>
      )}

      {mirando && <p className="tk-meta">Leyendo la planilla…</p>}

      {error && (
        /* `suave` por lo mismo que en SubirPlanilla: sin él, el aviso se abre
           encima de todo el hub. */
        <div className="panel-aviso suave">
          <p>{error}</p>
        </div>
      )}

      {hecho && (
        <div className="panel-aviso suave">
          <h3>Listo</h3>
          <p>{resumenDe(hecho)}</p>
          {hecho.fallaron?.length > 0 && (
            <p className="rto-tarde">
              No entraron {hecho.fallaron.length}: el archivo cambió entre el
              análisis y la confirmación. Volvé a subirlo.
            </p>
          )}
        </div>
      )}

      {analisis && (
        <>
          {analisis.pedido?.eco && (
            <p className="tk-meta">
              Entendí: <b>{analisis.pedido.eco}</b>
            </p>
          )}
          <p className="tk-meta">
            Pestaña <b>{analisis.hoja}</b> · {(analisis.nuevas || []).length}{" "}
            <b>nuevas</b> · {analisis.ya_estan || 0} ya estaban
            {(analisis.cambiadas || 0) > 0 && (
              <>
                {" "}
                · <b>{analisis.cambiadas}</b>{" "}
                {analisis.cambiadas === 1 ? "cambiada (pisa)" : "cambiadas (pisan)"}
              </>
            )}
            {analisis.pedido?.accion === "que_falta" && (
              <>
                {" "}
                · <b>{analisis.en_movimientos || 0}</b> ya viajan (Movimientos)
              </>
            )}{" "}
            · {analisis.cargados || 0} ya cargadas (van por Movimientos)
          </p>

          {analisis.aviso && <p className="rto-tarde">{analisis.aviso}</p>}

          {(analisis.nuevas || []).length > 0 && (
            <>
              <h3>Para la Previsión</h3>
              <p className="tk-meta">
                Vienen elegidas: es lo que la planilla dice y el sistema no
                tiene. Las marcadas <b>pisa la cargada</b> ya estaban con otro
                contenido y al confirmar se escriben encima — no queda la
                doble. Las notas del papel —paréntesis, «BI TREN», el rojo—
                viajan con cada fila.
              </p>
              <div className="tabla-wrap">
                <table className="tabla fija">
                  <thead>
                    <tr>
                      <th />
                      <th>Día</th>
                      <th>Acoplado</th>
                      <th>Tractor</th>
                      <th>Destino</th>
                      <th>Chofer</th>
                      <th>Cliente</th>
                      <th>Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analisis.nuevas || []).map((n) => (
                      <tr key={n.clave}>
                        <td>
                          <input
                            type="checkbox"
                            checked={nuevas.has(n.clave)}
                            onChange={() => alternar(n.clave)}
                            aria-label={`Cargar ${n.cliente || ""} del ${diaDe(n.fecha)}`}
                          />
                        </td>
                        <td>{diaDe(n.fecha)}</td>
                        <td>{vacio(n.acoplado)}</td>
                        <td>{vacio(n.tractor)}</td>
                        <td>{vacio(n.destino)}</td>
                        <td>{vacio(n.chofer)}</td>
                        <td>{vacio(n.cliente)}</td>
                        <td className="tenue">
                          {/* «pisa» (02/09): esta carga YA estaba con otro
                              contenido — al confirmar se escribe encima de
                              la versión cargada, no se suma otra fila. */}
                          {n.pisa && (
                            <>
                              <b>pisa la cargada</b>
                              {n.pisa.cambios ? `: ${n.pisa.cambios}` : ""}
                              {n.nota || n.en_movimientos ? " · " : ""}
                            </>
                          )}
                          {n.en_movimientos && (
                            <>
                              <b>ya viaja</b>
                              {n.nota ? " · " : ""}
                            </>
                          )}
                          {n.nota || (n.en_movimientos || n.pisa ? "" : "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(analisis.pendientes || []).length > 0 && (
            <>
              <h3>Pendientes de la planilla</h3>
              {/* Se muestran y NO se cargan: no tienen fecha, e inventarles
                  el día sería escribir un viaje que la planilla no dice. */}
              <p className="tk-meta">
                Están en la sección «PENDIENTES» del papel, sin fecha. Cuando
                Tráfico les ponga día van a aparecer en una previsión y entrar
                por acá — cargarlos hoy sería inventarles la fecha.
              </p>
              <div className="tabla-wrap">
                <table className="tabla fija">
                  <thead>
                    <tr>
                      <th>Acoplado</th>
                      <th>Tractor</th>
                      <th>Destino</th>
                      <th>Chofer</th>
                      <th>Cliente</th>
                      <th>Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analisis.pendientes || []).map((p, i) => (
                      <tr key={i}>
                        <td>{vacio(p.acoplado)}</td>
                        <td>{vacio(p.tractor)}</td>
                        <td>{vacio(p.destino)}</td>
                        <td>{vacio(p.chofer)}</td>
                        <td>{vacio(p.cliente)}</td>
                        <td className="tenue">{vacio(p.nota)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <button
            type="button"
            className="tk-boton tk-boton--fuerte"
            onClick={confirmar}
            disabled={cargando || nuevas.size === 0}
          >
            {cargando
              ? "Cargando…"
              : nuevas.size === 0
                ? "No hay nada elegido"
                : `Cargar ${nuevas.size} a Previsión`}
          </button>
        </>
      )}
    </div>
  );
};

export default SubirCargas;
